import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../gateway';
import { AUTH_CONSTANTS } from '../auth.consts';
import { AuthService, JwtTokens } from '../auth.service';

export const PASSWORDLESS_GENERIC_MESSAGE =
  'Se o destino estiver cadastrado e o e-mail confirmado, você receberá as instruções em instantes.';

interface EmailOtpVerifyResponse {
  readonly requiresWhatsapp: boolean;
  readonly recoveryToken?: string;
  readonly accessToken?: string;
  readonly refreshToken?: string;
}

/**
 * Calls Auth passwordless endpoints and completes the same JWT session as e-mail/senha.
 */
@Injectable({ providedIn: 'root' })
export class PasswordlessAuthService {
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);

  public async requestMagicLink(email: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_MAGIC_LINK_REQUEST,
        data: { email },
        base: 'auth',
      });
    } catch (error: unknown) {
      throw this.wrapError(error, 'Não foi possível enviar o link. Tente novamente em instantes.');
    }
  }

  public async verifyMagicLink(token: string): Promise<void> {
    try {
      const tokens = await this.apiService.post<JwtTokens>({
        path: AUTH_CONSTANTS.PATH_MAGIC_LINK_VERIFY,
        data: { token },
        base: 'auth',
      });
      this.authService.completeSession(tokens);
    } catch (error: unknown) {
      throw this.wrapError(error, 'Link inválido ou expirado. Solicite um novo.');
    }
  }

  public async requestEmailOtp(email: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_OTP_EMAIL_REQUEST,
        data: { email, purpose: 'login' },
        base: 'auth',
      });
    } catch (error: unknown) {
      throw this.wrapError(error, 'Não foi possível enviar o código. Tente novamente em instantes.');
    }
  }

  public async verifyEmailOtp(email: string, code: string): Promise<void> {
    try {
      const result = await this.apiService.post<EmailOtpVerifyResponse>({
        path: AUTH_CONSTANTS.PATH_OTP_EMAIL_VERIFY,
        data: { email, code, purpose: 'login' },
        base: 'auth',
      });
      this.completeFromOtp(result);
    } catch (error: unknown) {
      throw this.wrapError(error, 'Código inválido ou expirado. Solicite um novo.');
    }
  }

  public async requestWhatsappOtp(phone: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_OTP_WHATSAPP_REQUEST,
        data: { phone },
        base: 'auth',
      });
    } catch (error: unknown) {
      throw this.wrapError(error, 'WhatsApp indisponível no momento. Use o e-mail.');
    }
  }

  public async requestPhoneLink(phone: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_PHONE_LINK_REQUEST,
        data: { phone },
        base: 'auth',
      });
    } catch (error: unknown) {
      throw this.wrapError(error, 'Não foi possível enviar o código. Confirme o e-mail e tente de novo.');
    }
  }

  public async verifyPhoneLink(phone: string, code: string): Promise<void> {
    try {
      await this.apiService.post({
        path: AUTH_CONSTANTS.PATH_PHONE_LINK_VERIFY,
        data: { phone, code },
        base: 'auth',
      });
    } catch (error: unknown) {
      throw this.wrapError(error, 'Código inválido ou expirado. Solicite um novo.');
    }
  }

  public async verifyWhatsappOtp(phone: string, code: string): Promise<void> {
    try {
      const tokens = await this.apiService.post<JwtTokens>({
        path: AUTH_CONSTANTS.PATH_OTP_WHATSAPP_VERIFY,
        data: { phone, code },
        base: 'auth',
      });
      this.authService.completeSession(tokens);
    } catch (error: unknown) {
      throw this.wrapError(error, 'Código inválido ou expirado. Solicite um novo.');
    }
  }

  private completeFromOtp(result: EmailOtpVerifyResponse): void {
    if (!result.accessToken || !result.refreshToken) {
      throw new Error('Não foi possível entrar com este código. Confirme o e-mail e tente de novo.');
    }
    this.authService.completeSession({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
    });
  }

  private wrapError(error: unknown, fallbackMessage: string): Error {
    if (error instanceof Error && error.message.trim().length > 0) {
      return new Error(error.message, { cause: error });
    }
    return new Error(fallbackMessage, { cause: error });
  }
}
